import React, { useState, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { T } from '../../constants/mockData';
import Icon from '../../ui/Icon';
import { getPublicAppUrl, buildTicketUrl } from '../../config/publicUrl';

/* ── Default templates for every notification type ──────────────────────── */
const DEFAULT_TEMPLATES = {
  'New ticket created|Requester Notification': {
    subject: 'Your support request [{{ticket_number}}] has been received',
    body: `<p>Hi {{requester_name}},</p>
<p>Thank you for reaching out. Your support request has been received and a ticket has been created for you.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket Number</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Subject</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{ticket_subject}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Priority</td><td style="padding:10px 16px;">{{ticket_priority}}</td></tr>
</table>
<p>Track your request online:</p>
<p style="text-align:center;margin:20px 0;">
  <a href="{{ticket_url}}" style="display:inline-block;padding:12px 26px;background:#16a34a;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:8px;">View ticket {{ticket_number}}</a>
</p>
<p style="font-size:12px;color:#6b7280;text-align:center;"><a href="{{ticket_url}}" style="color:#16a34a;">{{ticket_url}}</a></p>
<p style="color:#9ca3af;font-size:12px;">{{workspace_name}} &nbsp;|&nbsp; This is an automated message. Please do not reply to this email.</p>`,
  },
  'Agent adds comment to Ticket|Requester Notification': {
    subject: 'Re: [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>Our support agent <strong>{{agent_name}}</strong> has added a comment to your ticket:</p>
<div style="background:#f0fdf4;border-left:4px solid #16a34a;padding:14px 18px;border-radius:0 8px 8px 0;margin:16px 0;">
  <p style="margin:0;color:#111827;">{{comment_body}}</p>
</div>
<p>To reply, simply respond to this email — your message will be added directly to ticket <strong>{{ticket_number}}</strong>.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Agent solves the Ticket|Requester Notification': {
    subject: '[Resolved] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>Great news! Your support ticket has been resolved by our team.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Resolved by</td><td style="padding:10px 16px;">{{agent_name}}</td></tr>
</table>
<p>If you feel your issue has not been fully resolved, please reply to this email and we will reopen the ticket.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Agent closes the Ticket|Requester Notification': {
    subject: '[Closed] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>Your support ticket <strong>{{ticket_number}}</strong> has been closed.</p>
<p>We hope your issue was resolved to your satisfaction. If you need further assistance, feel free to open a new ticket.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Requested Item Stage Change|Requester Notification': {
    subject: '[Update] [{{ticket_number}}] Your ticket status has changed',
    body: `<p>Hi {{requester_name}},</p>
<p>The status of your support request has been updated:</p>
<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:14px 18px;margin:16px 0;">
  <p style="margin:0;"><strong>Ticket:</strong> {{ticket_number}}</p>
  <p style="margin:8px 0 0;"><strong>New Status:</strong> {{ticket_status}}</p>
</div>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Ticket Approved or Rejected|Requester Notification': {
    subject: '[{{approval_status}}] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>Your support request <strong>{{ticket_number}}</strong> has been <strong>{{approval_status}}</strong>.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'User Invitation Email|Requester Notification': {
    subject: "You've been invited to {{workspace_name}}",
    body: `<p>Hi {{requester_name}},</p>
<p>You have been invited to join <strong>{{workspace_name}}</strong> as a support portal member.</p>
<p>Click the button below to set up your account:</p>
<p><a href="{{invite_link}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">Accept Invitation</a></p>
<p>Best regards,<br/>{{workspace_name}} Team</p>`,
  },
  'New ticket created|Agent Notification': {
    subject: '[New Ticket] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{agent_name}},</p>
<p>A new support ticket has been created and assigned to your queue.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Requester</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{requester_name}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Priority</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{ticket_priority}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Subject</td><td style="padding:10px 16px;">{{ticket_subject}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },
  'Ticket assigned to Group|Agent Notification': {
    subject: '[Group Assigned] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi Team,</p>
<p>A new ticket has been assigned to your group <strong>{{group_name}}</strong>.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Requester</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{requester_name}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Priority</td><td style="padding:10px 16px;">{{ticket_priority}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },
  'Ticket assigned to Agent|Agent Notification': {
    subject: '[Assigned to You] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{agent_name}},</p>
<p>A support ticket has been assigned to you and requires your attention.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Requester</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{requester_name}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Priority</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{ticket_priority}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Subject</td><td style="padding:10px 16px;">{{ticket_subject}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },

  /* ── Matrix notification templates ──────────────────────────────────── */
  'Ticket Created|Email Notification': {
    subject: 'Your support request [{{ticket_number}}] has been received',
    body: `<p>Hi {{requester_name}},</p>
<p>Thank you for reaching out. Your support request has been received and a ticket has been created.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket Number</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Subject</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{ticket_subject}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Priority</td><td style="padding:10px 16px;">{{ticket_priority}}</td></tr>
</table>
<p>Track your request online:</p>
<p style="text-align:center;margin:20px 0;">
  <a href="{{ticket_url}}" style="display:inline-block;padding:12px 26px;background:#16a34a;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:8px;">View ticket {{ticket_number}}</a>
</p>
<p style="font-size:12px;color:#6b7280;text-align:center;"><a href="{{ticket_url}}" style="color:#16a34a;">{{ticket_url}}</a></p>
<p style="color:#9ca3af;font-size:12px;">{{workspace_name}} &nbsp;|&nbsp; This is an automated message. Please do not reply to this email.</p>`,
  },
  'Ticket Assigned|Email Notification': {
    subject: '[Assigned] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{agent_name}},</p>
<p>A support ticket has been assigned to you.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Requester</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{requester_name}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Priority</td><td style="padding:10px 16px;">{{ticket_priority}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },
  'Comment Added|Email Notification': {
    subject: 'Re: [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>A new comment has been added to your ticket by <strong>{{agent_name}}</strong>:</p>
<div style="background:#f0fdf4;border-left:4px solid #16a34a;padding:14px 18px;border-radius:0 8px 8px 0;margin:16px 0;">
  <p style="margin:0;color:#111827;">{{comment_body}}</p>
</div>
<p>Reply to this email to respond directly to ticket <strong>{{ticket_number}}</strong>.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'SLA Warning|Email Notification': {
    subject: '[SLA Warning] Ticket {{ticket_number}} is approaching its SLA deadline',
    body: `<p>Hi {{agent_name}},</p>
<p>This is an automated reminder that ticket <strong>{{ticket_number}}</strong> is approaching its SLA deadline and requires your attention.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#fffbeb;border-radius:8px;border:1px solid #fcd34d;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #fcd34d;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #fcd34d;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #fcd34d;">Subject</td><td style="padding:10px 16px;border-bottom:1px solid #fcd34d;">{{ticket_subject}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Requester</td><td style="padding:10px 16px;">{{requester_name}}</td></tr>
</table>
<p>Please take action immediately to avoid an SLA breach.</p>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#d97706;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket Now</a></p>`,
  },
  'SLA Breach|Email Notification': {
    subject: '[SLA Breached] Ticket {{ticket_number}} has exceeded its SLA',
    body: `<p>Hi {{agent_name}},</p>
<p>Ticket <strong>{{ticket_number}}</strong> has breached its SLA deadline. Immediate action is required.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#fef2f2;border-radius:8px;border:1px solid #fca5a5;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #fca5a5;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #fca5a5;color:#dc2626;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #fca5a5;">Subject</td><td style="padding:10px 16px;border-bottom:1px solid #fca5a5;">{{ticket_subject}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #fca5a5;">Priority</td><td style="padding:10px 16px;border-bottom:1px solid #fca5a5;">{{ticket_priority}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Requester</td><td style="padding:10px 16px;">{{requester_name}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#dc2626;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },
  'Escalation|Email Notification': {
    subject: '[Escalated] Ticket {{ticket_number}} has been escalated',
    body: `<p>Hi {{agent_name}},</p>
<p>Ticket <strong>{{ticket_number}}</strong> has been escalated and requires urgent attention.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Subject</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{ticket_subject}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Requester</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{requester_name}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Priority</td><td style="padding:10px 16px;">{{ticket_priority}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },
  'Pending Customer|Email Notification': {
    subject: '[Action Required] Ticket {{ticket_number}} is awaiting your response',
    body: `<p>Hi {{requester_name}},</p>
<p>Your support ticket <strong>{{ticket_number}}</strong> is waiting for more information from you.</p>
<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:14px 18px;margin:16px 0;">
  <p style="margin:0;"><strong>Ticket:</strong> {{ticket_number}} — {{ticket_subject}}</p>
</div>
<p>Please reply to this email or visit the support portal to provide the required details so we can continue working on your request.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Resolved|Email Notification': {
    subject: '[Resolved] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>Great news! Your support ticket has been resolved.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Resolved by</td><td style="padding:10px 16px;">{{agent_name}}</td></tr>
</table>
<p>If your issue has not been fully resolved, please reply to this email and we will reopen the ticket.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Closed|Email Notification': {
    subject: '[Closed] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{requester_name}},</p>
<p>Your support ticket <strong>{{ticket_number}}</strong> has been closed. We hope your issue was resolved to your satisfaction.</p>
<p>If you need further assistance, feel free to open a new ticket at any time.</p>
<p>Best regards,<br/>{{workspace_name}} Support Team</p>`,
  },
  'Reopened|Email Notification': {
    subject: '[Reopened] [{{ticket_number}}] {{ticket_subject}}',
    body: `<p>Hi {{agent_name}},</p>
<p>Ticket <strong>{{ticket_number}}</strong> has been reopened and requires your attention.</p>
<table style="border-collapse:collapse;width:100%;margin:16px 0;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;width:140px;">Ticket</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;color:#16a34a;font-weight:700;">{{ticket_number}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;">Subject</td><td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;">{{ticket_subject}}</td></tr>
  <tr><td style="padding:10px 16px;font-weight:600;color:#374151;">Requester</td><td style="padding:10px 16px;">{{requester_name}}</td></tr>
</table>
<p><a href="{{ticket_url}}" style="display:inline-block;padding:10px 22px;background:#3B5BDB;color:#fff;font-weight:700;font-size:14px;text-decoration:none;border-radius:7px;">View Ticket</a></p>`,
  },
};

const MATRIX_EVENTS = [
  'Ticket Created', 'Ticket Assigned', 'Comment Added', 'SLA Warning', 'SLA Breach',
  'Escalation', 'Pending Customer', 'Resolved', 'Closed', 'Reopened',
];
const MATRIX_RECIPIENTS = ['Requester', 'Assigned Agent', 'Agent Group', 'Manager', 'Vendor'];

function cloneTemplateForRecipient(base, recipient) {
  if (!base) return { subject: '', body: '' };
  let { subject, body } = base;
  if (recipient === 'Requester') {
    if (/Hi \{\{agent_name\}\}/i.test(body) && !/Hi \{\{requester_name\}\}/i.test(body)) {
      body = body.replace(/Hi \{\{agent_name\}\}/gi, 'Hi {{requester_name}}');
      body = body.replace(/assigned to you/gi, 'assigned to our support team');
      body = body.replace(/requires your attention/gi, 'is being handled');
    }
    return { subject, body };
  }
  if (recipient === 'Agent Group') {
    body = body.replace(/Hi \{\{requester_name\}\}/gi, 'Hi {{group_name}}');
    body = body.replace(/Hi \{\{agent_name\}\}/gi, 'Hi {{group_name}}');
  } else {
    body = body.replace(/Hi \{\{requester_name\}\}/gi, 'Hi {{agent_name}}');
  }
  if (recipient === 'Manager' && !/\[Manager\]/i.test(subject)) {
    subject = `[Manager] ${subject}`;
  }
  if (recipient === 'Vendor' && !/\[Vendor\]/i.test(subject)) {
    subject = `[Vendor] ${subject}`;
  }
  return { subject, body };
}

/** Resolve template: Event|Recipient → legacy Event|Email Notification */
export function resolveDefaultTemplate(templateKey) {
  if (DEFAULT_TEMPLATES[templateKey]) return DEFAULT_TEMPLATES[templateKey];
  const pipe = templateKey.indexOf('|');
  if (pipe < 0) return { subject: '', body: '' };
  const event = templateKey.slice(0, pipe);
  const recipient = templateKey.slice(pipe + 1);
  const legacy = DEFAULT_TEMPLATES[`${event}|Email Notification`];
  return cloneTemplateForRecipient(legacy, recipient);
}

for (const event of MATRIX_EVENTS) {
  const base = DEFAULT_TEMPLATES[`${event}|Email Notification`];
  if (!base) continue;
  for (const recipient of MATRIX_RECIPIENTS) {
    const key = `${event}|${recipient}`;
    if (!DEFAULT_TEMPLATES[key]) {
      DEFAULT_TEMPLATES[key] = cloneTemplateForRecipient(base, recipient);
    }
  }
}

const RECIPIENT_PLACEHOLDER_HINTS = {
  Requester: ['{{requester_name}}', '{{requester_email}}', '{{ticket_number}}', '{{ticket_subject}}', '{{ticket_priority}}', '{{workspace_name}}'],
  'Assigned Agent': ['{{agent_name}}', '{{agent_email}}', '{{ticket_number}}', '{{ticket_subject}}', '{{requester_name}}', '{{ticket_url}}'],
  'Agent Group': ['{{group_name}}', '{{ticket_number}}', '{{ticket_subject}}', '{{requester_name}}', '{{ticket_priority}}', '{{ticket_url}}'],
  Manager: ['{{agent_name}}', '{{ticket_number}}', '{{ticket_subject}}', '{{requester_name}}', '{{ticket_priority}}'],
  Vendor: ['{{ticket_number}}', '{{ticket_subject}}', '{{requester_name}}', '{{workspace_name}}'],
};

/* ── Placeholder variables grouped by category ──────────────────────────── */
const PLACEHOLDER_GROUPS = [
  {
    label: 'Ticket',
    color: '#16a34a',
    bg: '#f0fdf4',
    items: ['{{ticket_number}}', '{{ticket_subject}}', '{{ticket_status}}', '{{ticket_priority}}', '{{ticket_url}}'],
  },
  {
    label: 'Requester',
    color: '#2563eb',
    bg: '#eff6ff',
    items: ['{{requester_name}}', '{{requester_email}}'],
  },
  {
    label: 'Agent',
    color: '#7c3aed',
    bg: '#f5f3ff',
    items: ['{{agent_name}}', '{{agent_email}}', '{{group_name}}'],
  },
  {
    label: 'System',
    color: '#d97706',
    bg: '#fffbeb',
    items: ['{{workspace_name}}', '{{comment_body}}', '{{approval_status}}', '{{invite_link}}'],
  },
];

/* ── Quill toolbar config ───────────────────────────────────────────────── */
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'align',
  'link', 'image', 'blockquote', 'code-block',
];

/* ── Preview modal ──────────────────────────────────────────────────────── */
function PreviewModal({ subject, body, onClose }) {
  const sampleTicket = 'SR2026/05/000006';
  const portalBase = getPublicAppUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
  const sampleVars = {
    '{{ticket_number}}': sampleTicket,
    '{{ticket_subject}}': 'VPN Connection Fails for Remote Users',
    '{{ticket_status}}': 'Open',
    '{{ticket_priority}}': 'High',
    '{{ticket_url}}': buildTicketUrl(sampleTicket),
    '{{requester_name}}': 'Demo User 1',
    '{{requester_email}}': 'demo.user1@sysnet.com.sg',
    '{{agent_name}}': 'Support Lead',
    '{{agent_email}}': 'support.lead1@sysnet.com.sg',
    '{{group_name}}': 'Network Support Team',
    '{{workspace_name}}': 'PowerDesk',
    '{{comment_body}}': 'We have reviewed your request and are working on a fix.',
    '{{approval_status}}': 'Approved',
    '{{invite_link}}': `${portalBase}/invite/abc123`,
  };
  const render = (str) => Object.entries(sampleVars).reduce((s, [k, v]) => s.replaceAll(k, v), str || '');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: 680, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.22)' }}>
        {/* Modal header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>Email Preview</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>Shown with sample data substituted in</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Icon name="close" size={16} color="#64748b" />
          </button>
        </div>

        {/* Email mockup */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Email client chrome */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', fontFamily: 'Arial, sans-serif' }}>
            {/* Top bar */}
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 48 }}>From</span>
                <span style={{ fontSize: 13, color: '#374151' }}>PowerDesk Support &lt;support@sysnet.com.sg&gt;</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 48 }}>To</span>
                <span style={{ fontSize: 13, color: '#374151' }}>demo.user1@sysnet.com.sg</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 48 }}>Subject</span>
                <span style={{ fontSize: 13, color: '#1A202C', fontWeight: 600 }}>{render(subject)}</span>
              </div>
            </div>
            {/* Body */}
            <div
              style={{ padding: '20px 24px', fontSize: 14, lineHeight: 1.7, color: '#1A202C', background: '#fff' }}
              dangerouslySetInnerHTML={{ __html: render(body) }}
            />
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '9px 24px', border: 'none', borderRadius: 8, background: T.active, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Close Preview</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────── */
export default function EmailTemplateEditor({ item, templateKey, templates, onSave, onBack, recipient }) {
  const defaultTpl = resolveDefaultTemplate(templateKey);
  const saved = templates[templateKey] || defaultTpl;
  const hintPlaceholders = recipient ? (RECIPIENT_PLACEHOLDER_HINTS[recipient] || []) : [];
  const [subject, setSubject] = useState(saved.subject);
  const [body, setBody] = useState(saved.body);
  const [showPreview, setShowPreview] = useState(false);
  const [saved2, setSaved2] = useState(false);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'html'
  const quillRef = useRef(null);

  // Insert placeholder at cursor position in Quill
  const insertPlaceholder = (placeholder) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.insertText(range ? range.index : quill.getLength(), placeholder);
  };

  const handleSave = () => {
    onSave(templateKey, { subject, body });
    setSaved2(true);
    setTimeout(() => setSaved2(false), 2500);
  };

  const handleReset = () => {
    const def = resolveDefaultTemplate(templateKey);
    setSubject(def.subject);
    setBody(def.body);
  };

  const isModified =
    subject !== saved.subject || body !== saved.body;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexShrink: 0, flexWrap: 'wrap' }}>
        <button
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: '#374151', fontWeight: 600, fontSize: 13, flexShrink: 0 }}
        >
          <Icon name="back" size={14} color="#374151" /> Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>
            {item.group} &rsaquo; <span style={{ color: T.active }}>{item.label}</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C', marginTop: 1 }}>
            {recipient ? `${recipient} template` : 'Edit Email Template'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleReset}
            style={{ padding: '8px 16px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            Reset to Default
          </button>
          <button
            onClick={() => setShowPreview(true)}
            style={{ padding: '8px 16px', border: `1px solid ${T.active}`, borderRadius: 8, background: '#eff6ff', color: T.active, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Icon name="eye" size={14} color={T.active} /> Preview
          </button>
          <button
            onClick={handleSave}
            style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: saved2 ? '#16a34a' : T.active, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background .2s' }}
          >
            {saved2 ? '✓ Saved!' : 'Save Template'}
          </button>
        </div>
      </div>

      {recipient && (
        <div style={{
          marginBottom: 14, padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe',
          borderRadius: 10, fontSize: 13, color: '#1e40af', lineHeight: 1.55, flexShrink: 0,
        }}>
          <strong>{recipient}</strong> emails are filled in automatically when sent — click placeholders on the right
          (e.g. <code style={{ fontSize: 12 }}>{'{{requester_name}}'}</code>) to insert dynamic values.
          {hintPlaceholders.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {hintPlaceholders.map(ph => (
                <button
                  key={ph}
                  type="button"
                  onClick={() => insertPlaceholder(ph)}
                  style={{
                    padding: '3px 8px', borderRadius: 6, border: '1px solid #93c5fd', background: '#fff',
                    fontSize: 11, fontWeight: 600, color: '#2563eb', cursor: 'pointer', fontFamily: 'monospace',
                  }}
                >
                  {ph}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Two-column layout ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>

        {/* Left: editor column */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Subject line */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', flexShrink: 0 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>
              Subject Line
            </label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Email subject…"
              style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', color: '#1A202C' }}
              onFocus={e => (e.target.style.borderColor = T.active)}
              onBlur={e => (e.target.style.borderColor = T.border)}
            />
          </div>

          {/* Body editor */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Tab bar */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${T.border}`, padding: '0 16px', gap: 0, flexShrink: 0 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 16, paddingTop: 2 }}>
                Body
              </label>
              {['editor', 'html'].map(t => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{ padding: '10px 14px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: activeTab === t ? 700 : 500, color: activeTab === t ? T.active : '#718096', cursor: 'pointer', borderBottom: activeTab === t ? `2px solid ${T.active}` : '2px solid transparent', marginBottom: -1 }}
                >
                  {t === 'editor' ? 'Visual Editor' : 'HTML Source'}
                </button>
              ))}
            </div>

            {activeTab === 'editor' ? (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <style>{`
                  .ql-container { font-family: inherit !important; font-size: 14px !important; border: none !important; }
                  .ql-toolbar { border: none !important; border-bottom: 1px solid #e5e7eb !important; background: #fafafa; padding: 6px 10px !important; }
                  .ql-editor { padding: 16px 18px !important; min-height: 240px; }
                  .ql-editor p { margin: 0 0 10px; }
                  .ql-editor:focus { outline: none; }
                `}</style>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}
                />
              </div>
            ) : (
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                spellCheck={false}
                style={{ flex: 1, border: 'none', padding: '14px 16px', fontFamily: '"Fira Code", "Courier New", monospace', fontSize: 12, lineHeight: 1.6, color: '#1A202C', background: '#fafafa', resize: 'none', outline: 'none' }}
              />
            )}
          </div>

          {isModified && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', flexShrink: 0 }}>
              <Icon name="info" size={14} color="#d97706" />
              You have unsaved changes — click <strong style={{ marginLeft: 3 }}>Save Template</strong> to apply.
            </div>
          )}
        </div>

        {/* Right: placeholder variables sidebar */}
        <div style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Placeholders
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14, lineHeight: 1.5 }}>
              Click a placeholder to insert it at the cursor position in the editor.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {PLACEHOLDER_GROUPS.map(group => (
                <div key={group.label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: group.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{group.label}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {group.items.map(ph => (
                      <button
                        key={ph}
                        onClick={() => insertPlaceholder(ph)}
                        title={`Insert ${ph}`}
                        style={{ textAlign: 'left', background: group.bg, border: `1px solid ${group.color}33`, borderRadius: 6, padding: '5px 9px', fontSize: 11, color: group.color, fontWeight: 600, cursor: 'pointer', fontFamily: '"Fira Code","Courier New",monospace', transition: 'opacity .15s' }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                      >
                        {ph}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips card */}
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 14, flexShrink: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 6 }}>💡 Tips</div>
            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#166534', lineHeight: 1.7 }}>
              <li>Click a placeholder to insert it at the cursor</li>
              <li>Use <strong>HTML Source</strong> to paste raw HTML</li>
              <li>Use <strong>Preview</strong> to see sample output</li>
            </ul>
          </div>
        </div>
      </div>

      {showPreview && (
        <PreviewModal subject={subject} body={body} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}
